import { useCallback, useLayoutEffect, useRef, useState } from "react";
import "./StageDrag.css";

const DRAG_THRESHOLD_PX = 6;
const RETURN_MS = 180;

function isInteractiveTarget(target) {
  return Boolean(target?.closest?.("button, input, a, textarea, [role=dialog]"));
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
}

function clearDragMarkers() {
  document.querySelectorAll('[data-dragging="true"]').forEach(el => el.removeAttribute("data-dragging"));
}

function markDragTargets(ids) {
  ids.forEach(id => {
    const el = document.querySelector(`[data-drag-id="${id}"]`);
    if (!el) return;
    const wrapper = el.closest(".promotion-stage-item") || el;
    wrapper.setAttribute("data-dragging", "true");
  });
}

function buildCloneDom(root, session, labels) {
  root.innerHTML = "";
  const { cardEl, ids } = session;
  if (ids.length > 2) {
    const l2 = document.createElement("div");
    l2.className = "promotion-drag-stack l2";
    root.appendChild(l2);
  }
  if (ids.length > 1) {
    const l1 = document.createElement("div");
    l1.className = "promotion-drag-stack l1";
    root.appendChild(l1);
  }
  const clone = cardEl.cloneNode(true);
  clone.removeAttribute("data-dragging");
  clone.removeAttribute("data-drag-id");
  clone.setAttribute("aria-hidden", "true");
  clone.classList.add("promotion-drag-card");
  clone.querySelectorAll("input, button, a, textarea").forEach(node => { node.tabIndex = -1; });
  root.appendChild(clone);
  if (ids.length > 1 && typeof labels?.count === "function") {
    const count = document.createElement("span");
    count.className = "promotion-drag-count";
    count.textContent = labels.count(ids.length);
    root.appendChild(count);
  }
}

/**
 * Pointer-basiertes Drag-and-drop fuer das Stage-Board. Liefert Props fuer die
 * Kartenwrapper, einen sichtbaren Ziehstatus fuer das Hervorheben der Zielspalte
 * und eine imperativ befuellte Geisterschicht (Kartenklon), die der Aufrufer
 * innerhalb von `.promotion-page` rendert, damit die Promotion-Tokens greifen.
 */
export default function useStageDrag({ enabled = true, canDrag, targetStage, dragSet, onDrop, labels } = {}) {
  const optsRef = useRef({});
  optsRef.current = { enabled, canDrag, targetStage, dragSet, onDrop, labels };

  const [dragging, setDragging] = useState(null);
  const [frame, setFrame] = useState(null);
  const [announcement, setAnnouncement] = useState("");

  const sessionRef = useRef(null);
  const cloneRootRef = useRef(null);
  const layerRef = useRef(null);
  const builtForRef = useRef(null);
  const attachedRef = useRef(false);

  const finishDragRef = useRef(null);
  const handleMoveRef = useRef(null);
  const handleUpRef = useRef(null);
  const handleCancelRef = useRef(null);
  const handleKeyDownRef = useRef(null);

  function detachListeners() {
    if (!attachedRef.current) return;
    document.removeEventListener("pointermove", handleMoveRef.current);
    document.removeEventListener("pointerup", handleUpRef.current);
    document.removeEventListener("pointercancel", handleCancelRef.current);
    document.removeEventListener("keydown", handleKeyDownRef.current);
    attachedRef.current = false;
  }

  finishDragRef.current = (returning, reason) => {
    const session = sessionRef.current;
    if (!session) return;
    clearDragMarkers();
    detachListeners();
    if (session.active) document.body.style.cursor = session.prevBodyCursor ?? "";
    setAnnouncement(reason === "cancel" ? (optsRef.current.labels?.cancelled || "") : "");
    const release = () => { setFrame(null); setDragging(null); sessionRef.current = null; };
    if (returning && session.active && session.originRect && !prefersReducedMotion()) {
      setFrame({ ...session.originRect, returning: true });
      window.setTimeout(release, RETURN_MS);
    } else {
      release();
    }
  };

  handleKeyDownRef.current = event => {
    if (event.key === "Escape" && sessionRef.current) finishDragRef.current(true, "cancel");
  };
  handleCancelRef.current = () => {
    if (sessionRef.current) finishDragRef.current(true, "cancel");
  };
  handleUpRef.current = () => {
    const session = sessionRef.current;
    if (!session) return;
    if (!session.active) { finishDragRef.current(false, "click"); return; }
    const { ids, targetStageKey, over } = session;
    finishDragRef.current(!over, over ? "drop" : "drop-invalid");
    if (over) optsRef.current.onDrop?.(ids, targetStageKey);
  };
  handleMoveRef.current = event => {
    const session = sessionRef.current;
    if (!session) return;
    if (!session.active) {
      const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
      if (distance < DRAG_THRESHOLD_PX) return;
      activateDrag(session);
    }
    updateDragPosition(session, event);
  };

  function activateDrag(session) {
    session.active = true;
    try { session.cardEl.setPointerCapture?.(session.pointerId); } catch { /* kein Zeigerfang verfuegbar */ }
    const rect = session.cardEl.getBoundingClientRect();
    session.offX = session.startX - rect.left;
    session.offY = session.startY - rect.top;
    session.originRect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    session.prevBodyCursor = document.body.style.cursor;
    document.body.style.cursor = "grabbing";
    markDragTargets(session.ids);
    setAnnouncement("");
    setFrame({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    setDragging({ ids: session.ids, targetStage: session.targetStageKey, over: false });
  }

  function updateDragPosition(session, event) {
    setFrame({ left: event.clientX - session.offX, top: event.clientY - session.offY, width: session.originRect.width, height: session.originRect.height });
    const layerEl = layerRef.current;
    const previousDisplay = layerEl ? layerEl.style.display : null;
    if (layerEl) layerEl.style.display = "none";
    const under = document.elementFromPoint(event.clientX, event.clientY);
    if (layerEl) layerEl.style.display = previousDisplay || "";
    const over = Boolean(under?.closest?.(`[data-stage="${session.targetStageKey}"]`));
    if (over !== session.over) {
      session.over = over;
      setDragging({ ids: session.ids, targetStage: session.targetStageKey, over });
    }
  }

  useLayoutEffect(() => {
    const root = cloneRootRef.current;
    if (!root) return;
    if (!dragging || !sessionRef.current) {
      if (builtForRef.current) { root.innerHTML = ""; builtForRef.current = null; }
      return;
    }
    if (builtForRef.current === dragging.ids) return;
    builtForRef.current = dragging.ids;
    buildCloneDom(root, sessionRef.current, optsRef.current.labels);
  }, [dragging]);

  useLayoutEffect(() => () => detachListeners(), []);

  const dragProps = useCallback(row => {
    const opts = optsRef.current;
    const draggable = Boolean(opts.enabled && opts.canDrag?.(row) && opts.targetStage?.(row));
    return {
      "data-draggable": draggable ? "true" : undefined,
      onPointerDown: event => {
        const opts = optsRef.current;
        if (!opts.enabled) return;
        if (event.button !== 0) return;
        if (isInteractiveTarget(event.target)) return;
        if (!opts.canDrag?.(row)) return;
        const targetStageKey = opts.targetStage?.(row);
        if (!targetStageKey) return;
        const ids = opts.dragSet ? opts.dragSet(row) : [row.id];
        if (!ids?.length) return;
        sessionRef.current = {
          row, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
          cardEl: event.currentTarget, active: false, ids, targetStageKey, over: false,
        };
        document.addEventListener("pointermove", handleMoveRef.current);
        document.addEventListener("pointerup", handleUpRef.current);
        document.addEventListener("pointercancel", handleCancelRef.current);
        document.addEventListener("keydown", handleKeyDownRef.current);
        attachedRef.current = true;
      },
    };
  }, []);

  const layer = <>
    <div ref={layerRef} className={`promotion-drag-layer${frame?.returning ? " is-returning" : ""}`} aria-hidden="true"
      style={frame ? { display: "block", left: `${frame.left}px`, top: `${frame.top}px`, width: `${frame.width}px`, height: `${frame.height}px` } : undefined}>
      <div className="promotion-drag-clone-root" ref={cloneRootRef}/>
    </div>
    <div className="promotion-drag-status" role="status" aria-live="polite">{announcement}</div>
  </>;

  return { dragProps, dragging, layer };
}
